/* eslint no-param-reassign: 0 */
import {
	ProjectService, ClientService, cachedEndpointsService, InvoiceService, UserService,
} from '@services';
import {
	forEach, cloneDeep, filter, isEmpty, isUndefined, get, omit,
} from 'lodash';
import queryString from 'query-string';
import { push } from 'react-router-redux';
import { apiErrorHandler } from '@config/utils';
import { processUrl as genericProcessUrl } from '@redux-core/helpers';
import utils, { constants } from '@utils';

import axios from 'axios';
import Actions, { INITIAL_STATE as paymentsInitialState } from '.';

const PAYMENTS_FILTERS = [
	'sortedColumn',
	'asc',
	'displayCount',
	'pageNumber',
	'locationId',
];

let cancel;

const getInvoiceListData = (viewType = 'payment') => ((dispatch, getState) => {
	dispatch(Actions.setLoading('listData', true));
	const {
		payments : {
			filters,
			data : {
				pagingToken, hasNext,
			},
		},
	} = getState();

	// If there is a request already running, abort it and start a new one
	if (!isUndefined(cancel)) cancel();
	const status = constants.VIEW_INVOICE_STATUS[viewType].value;
	if (viewType !== 'balance') {
		const invoiceEndDate = new Date().toLocaleDateString('en-CA', { month : 'numeric', day : 'numeric', year : 'numeric' });
		const sixMonthsAgo = new Date(new Date().setMonth(new Date().getMonth() - 6));
		const invoiceStartDate = sixMonthsAgo.toLocaleDateString('en-CA', { month : 'numeric', day : 'numeric', year : 'numeric' });
		InvoiceService.getInvoiceList({
			getCancelToken   : (c) => { cancel = c; },
			status,
			...filters,
			sortBy           : filters.sortedColumn,
			asc              : viewType === 'paid' ? false : filters.asc,
			invoiceStartDate : viewType === 'paid' ? invoiceStartDate : filters.invoiceStartDate,
			invoiceEndDate   : viewType === 'paid' ? invoiceEndDate : filters.invoiceEndDate,
		})
			.then(({ data : { ItemList, Count } }) => {
				dispatch(Actions.resetPaymentsValues(['selectAll']));
				// Receive data
				dispatch(Actions.receiveData([
					{ property : 'listData', value : ItemList.map((item) => ({ ...item, Checked : false })) },
					{ property : 'totalCount', value : Count },
				]));
				dispatch(Actions.updateStoreProperties({
					totalCountForCheckbox : viewType === 'disputes' ? 0 : utils.getPaymentListTotalForCheckbox(ItemList),
				}));
			})
			.catch((error) => {
				if (!axios.isCancel(error)) {
					apiErrorHandler({
						dispatch,
						module         : 'payments',
						property       : 'loading.listData',
						defaultMessage : 'Error loading invoices',
					})(error);
				}
			})
			.finally(() => {
				dispatch(Actions.setLoading('listData', false));
			});
	} else {
		const queryParams = {};
		if (hasNext && filters.pageNumber > 1) {
			queryParams.PagingToken = pagingToken;
		}
		UserService.getBalanceTransactionHistory(queryParams)
			.then(({
				data : {
					ItemList, Count, PagingToken, HasNext,
				},
			}) => {
				dispatch(Actions.setLoading('listData', false));
				// Receive data
				dispatch(Actions.receiveData([
					{ property : 'listData', value : ItemList },
					{ property : 'totalCount', value : Count },
					{ property : 'pagingToken', value : PagingToken },
					{ property : 'hasNext', value : HasNext },
				]));
			})
			.catch((error) => {
				if (!axios.isCancel(error)) {
					apiErrorHandler({
						dispatch,
						module         : 'payments',
						property       : 'loading.listData',
						defaultMessage : 'Error loading invoices',
					})(error);
				}
			});
	}
});

const getInvoiceData = (id) => ((dispatch) => {
	dispatch(Actions.setLoading('details', true));
	InvoiceService.getInvoiceData(id)
		.then(({ data }) => {
			dispatch(Actions.setLoading('details', false));
			dispatch(Actions.updateStoreProperties({ details : data }));
		})
		.catch(apiErrorHandler({
			dispatch,
			module   : 'payments',
			property : 'loading.details',
		}));
});

const getInvoiceListCounts = () => ((dispatch, getState) => {
	const {
		payments : { filters },
	} = getState();
	dispatch(Actions.setLoading('counts', true));
	Promise.all(
		[
			InvoiceService.getCounts({ queryParams : filters }),
			UserService.getAccountBalance(),
		],
	).then((
		[
			{ data : { DisputedCount, PendingReviewTotalAmount, PendingPaymentTotalAmount } },
			{ data : balance },
		],
	) => {
		const {
			payments : { counts, officerOrAgency },
		} = getState();

		const countsObj = {
			review  : PendingReviewTotalAmount,
			payment : officerOrAgency === 'officer' ? PendingPaymentTotalAmount : counts.payment,
			dispute : DisputedCount,
			balance,
		};
		dispatch(Actions.updateStoreProperties({ counts : countsObj }));

		// Receive data
		dispatch(Actions.receiveData([{ property : 'counts', value : countsObj }]));
	})
		.catch(apiErrorHandler({
			dispatch,
			module   : 'payments',
			property : 'loading.counts',
		}))
		.finally(() => {
			dispatch(Actions.setLoading('counts', false));
		});
});

export const getClients = () => ((dispatch) => {
	// Start loading animation
	dispatch(Actions.setLoading('clients', true));

	cachedEndpointsService.getClients()
		.then(({ data }) => {
			const items = data.map(({ Id : value, Name : label }) => ({
				value,
				label,
			}));

			// Receive data
			dispatch(Actions.receiveData([{ property : 'clients', value : items }]));

			// Stop loading animation
			dispatch(Actions.setLoading('clients', false));
		})
		.catch(apiErrorHandler({
			dispatch,
			module   : 'payments',
			property : 'loading.clients',
		}));
});

export const getProjects = (client) => ((dispatch, getState) => {
	const {
		payments : {
			filters : {
				clientId : id,
				includeInactive,
			},
		},
	} = getState();

	const clientId = client || (!isUndefined(id) ? id : undefined);

	// Start loading animation
	dispatch(Actions.setLoading('projects', true));

	const includeAll = !isUndefined(includeInactive) ? includeInactive : false;

	ProjectService.getAll({
		includeAll,
		clientId,
	})
		.then(({ data : { ItemList } }) => {
			const projects = ItemList.map(({ Id : value, Name : label }) => ({ value, label }));

			const propsToUpdate = [{ property : 'projects', value : projects }];

			dispatch(Actions.setLoading('projects', false));

			// Receive data
			dispatch(Actions.receiveData(propsToUpdate));
		})
		.catch(apiErrorHandler({
			dispatch,
			module         : 'payments',
			property       : 'loading.projects',
			defaultMessage : 'Error loading projects',
		}));
});

export const getLocations = () => ((dispatch, getState) => {
	const {
		filters : {
			clientId,
		},
	} = getState().payments;

	if (!clientId) return;

	// Start loading animation
	dispatch(Actions.setLoading('locations', true));

	ClientService.getLocations({ id : clientId })
		.then(({ data }) => {
			const items = data.map(({ Id : value, Name : label }) => ({
				value,
				label,
			}));

			// Receive data
			dispatch(Actions.receiveData([{ property : 'locations', value : items }]));

			// Stop loading animation
			dispatch(Actions.setLoading('locations', false));
		})
		.catch(apiErrorHandler({
			dispatch,
			module         : 'payments',
			property       : ['loading', 'locations'],
			defaultMessage : 'Error loading locations',
		}));
});

const getDisputeReasons = () => ((dispatch) => {
	InvoiceService.getDisputeReasons()
		.then(({ data }) => {
			const newData = utils.buildSelectArrayOptionsFromAPIResponse({
				data,
				labelProperty : 'Reason',
				valueProperty : 'Id',
			});
			dispatch(Actions.updateStoreProperties({ disputeReasons : newData }));
		})
		.catch(apiErrorHandler({
			dispatch,
			module : 'payments',
		}));
});

const submitDisputeForm = (Id, Note, DisputeReasonId) => ((dispatch) => {
	dispatch(Actions.setLoading('disputeForm', true));
	InvoiceService.submitDisputeForm(Id, Note, DisputeReasonId)
		.then(() => {
			dispatch(Actions.updateStoreProperties({ disputeConfirmation : true }));
			dispatch(Actions.setLoading('disputeForm', false));
		})
		.catch(apiErrorHandler({
			dispatch,
			module   : 'payments',
			property : 'loading.disputeForm',
		}));
});

export const applyCriteria = (criteriaCollection) => ((dispatch, getState) => {
	const {
		router : {
			location : {
				pathname : rawPathname,
				search,
			},
		},
	} = getState();

	const { filters : initialFilters } = paymentsInitialState;
	const query = !isUndefined(search) ? queryString.parse(search) : {};
	const filterCollection = [];
	const dataCollection = [];
	const pathname = rawPathname;

	forEach(criteriaCollection, ({ property, value }) => {
		filterCollection.push({
			property,
			value,
		});

		query[property] = value;

		switch (property) {
			case 'clientId': {
				delete query.locationId;

				filterCollection.push({ property : 'locationId', value : null });

				dataCollection.push({ property : 'projects', value : [] });
				dataCollection.push({ property : 'locations', value : [] });

				if (value === initialFilters.pageNumber) delete query.page;
				break;
			}
			default:
				break;
		}

		if (property !== 'pageNumber') {
			delete query.pageNumber;
			filterCollection.push({ property : 'pageNumber', value : 1 });
		}

		/**
		 * Delete empty query params
		 * if there are any
		 */
		if (
			(
				property !== 'pageNumber'
				|| property !== 'displayCount'
				|| property !== 'sortBy'
			)
			&& !value
		) { delete query[property]; }
	});

	dispatch(Actions.updateFilters(filterCollection));
	dispatch(Actions.receiveData(dataCollection));

	dispatch(push({
		pathname,
		search : queryString.stringify(query),
	}));

	/* eslint-disable-next-line */
	dispatch(processUrl(true));
});

export const processUrl = () => ((dispatch, getState) => {
	const {
		router: { location : { pathname } },
	} = getState();

	const nonIntegerQueries = [
		'asc',
		'sortBy',
		'sortedColumn',
		'jobStartDate',
		'jobEndDate',
		'invoiceStartDate',
		'invoiceEndDate',
		'prePay',
	];

	const callback = (baseFilterCollection) => {
		const filtersCollection = [];

		dispatch(Actions.updateFilters([...baseFilterCollection, ...filtersCollection]));

		// Request data
		dispatch(getInvoiceListData(get(pathname.split('/'), [3], '')));
		dispatch(getClients());
		dispatch(getLocations());
		dispatch(getInvoiceListCounts());
	};

	// Clear list before getting new data
	dispatch(Actions.receiveData([{ property : 'listData', value : [] }]));
	dispatch(genericProcessUrl(callback, nonIntegerQueries));
});

export const resetCriteria = () => ((dispatch, getState) => {
	const {
		router : {
			location : { pathname },
		},
	} = getState();

	// Reset filters
	dispatch(Actions.resetPaymentsFilter());

	// Also reset data
	dispatch(Actions.receiveData([{ property : 'listData', value : [] }]));

	// Update URL
	dispatch(push({
		pathname,
		query : {},
	}));

	// Process new URL
	dispatch(processUrl(true));
});

const callStatusChangedApi = ({ idArray, status, viewType }, modalName) => ((dispatch, getState) => {
	const { approvedIndividual, successfulUpdateIds } = getState().payments;
	dispatch(Actions.setLoading('status', true));
	let message = 'Invoice successfully updated';
	// Process each selected invoice progressively as opposed to sending a single list of Ids
	idArray.map((id) => (() => InvoiceService.changeInvoiceUserStatus({ idArray : [id], status })))
		.reduce((prev, task, idx) => (prev.then(task).then(() => {
			successfulUpdateIds.push(idArray[idx]);
			dispatch(Actions.updateStoreProperties({ successfulUpdateIds }));
			return Promise.resolve(successfulUpdateIds);
		}).catch(() => Promise.resolve(successfulUpdateIds))
		), Promise.resolve())
		.then((result) => {
			// `result` is the array of invoices that were successfully updated
			if (result.length === 0) {
				// If no invoices were updated, throw an error and stop executing further code
				message = `Error updating${idArray.length > 1 ? ' selected' : ''} invoices. Please try again.`;
				apiErrorHandler({
					dispatch,
					module         : 'payments',
					property       : 'loading.data',
					defaultTitle   : 'Error',
					defaultMessage : message,
				});
				return;
			}
			if (result.length > 1) {
				message = `${result.length} invoices succesfully updated`;
			}
			utils.notify({
				type  : 'success',
				message,
				title : 'Payments',
			});

			if (modalName) {
				dispatch(Actions.updateModalState(modalName, true));
				dispatch(Actions.updateStoreProperties({ activeInvoiceIds : result }));
			}

			dispatch(push({ pathname : `/payments/list/${!isUndefined(viewType) ? viewType : ''}` }));
			if (approvedIndividual) dispatch(Actions.updateStoreProperties({ approvedIndividual : false }));

			// Reset the updates array
			dispatch(Actions.updateStoreProperties({ successfulUpdateIds : [] }));
			dispatch(resetCriteria());
		})
		.finally(() => {
			dispatch(Actions.setLoading('status', false));
		});
});

const getInvoiceSummary = () => ((dispatch, getState) => {
	const { filters, activeInvoiceIds } = getState().payments;
	const invoiceFilters = omit(filters, ['displayCount', 'pageNumber', 'sortBy', 'asc']);
	const params = {
		queryParams : !isEmpty(activeInvoiceIds) ? null : invoiceFilters,
		data        : isEmpty(activeInvoiceIds) ? null : { InvoiceIds : activeInvoiceIds },
	};

	dispatch(Actions.setLoading('invoiceSummary', true));

	InvoiceService.getInvoiceSummary(params)
		.then(({ data }) => {
			dispatch(Actions.setLoading('invoiceSummary', false));
			dispatch(Actions.updateStoreProperties({ invoiceSummary : data }));
			const { TransactionSummaryItems } = data;
			if (TransactionSummaryItems.length && TransactionSummaryItems[0].InvoiceStatusId === 6) {
				utils.notify({
					type    : 'error',
					title   : 'Error',
					message : 'This invoice is already paid',
				});
			}
		})
		.catch(apiErrorHandler({
			dispatch,
			module   : 'payments',
			property : 'loading.invoiceSummary',
		}));
});

const getReceiptDetails = (transactionId) => ((dispatch) => {
	dispatch(Actions.setLoading('receipt', true));
	InvoiceService.getReceiptDetails(transactionId)
		.then(({ data }) => {
			dispatch(Actions.setLoading('receipt', false));
			dispatch(Actions.updateStoreProperties({ receipt : data }));
			dispatch(Actions.resetPaymentsValues(['activeTransactionId']));
		})
		.catch(apiErrorHandler({
			dispatch,
			module   : 'payments',
			property : 'loading.receipt',
		}));
});

const invoiceStatusChanged = ({
	type, id, viewType, isPrepaid,
}) => ((dispatch, getState) => {
	const { data : { listData : data } } = getState().payments;

	if (type === 'bulk') {
		const idArray = data.map((item) => item.Id);
		dispatch(Actions.updateStoreProperties({ activeInvoiceIds : idArray }));
		if (viewType === 'review') {
			dispatch(callStatusChangedApi({
				idArray,
				viewType,
				status : constants.INVOICE_STATUSES.PENDING_PAYMENT,
			}, isPrepaid ? null : 'paymentConfirmation'));
		} else if (viewType === 'payment') {
			dispatch(push({ pathname : '/payments/invoice', search : queryString.stringify({ payAll : true }) }));
		}

		return;
	}

	const idArray = [];
	if (type === 'individual') {
		dispatch(Actions.updateStoreProperties({ approvedIndividual : true }));
		idArray.push(id);
	} else {
		forEach(data, (item) => {
			if (item.Checked) idArray.push(item.Id);
		});
		if (!idArray.length) {
			utils.notify({
				type    : 'error',
				title   : 'Error',
				message : 'Please select at least 1 invoice',
			});
			return;
		}
	}
	dispatch(Actions.updateStoreProperties({ activeInvoiceIds : idArray }));
	if (viewType === 'review') {
		dispatch(callStatusChangedApi({ idArray, status : constants.INVOICE_STATUSES.PENDING_PAYMENT, viewType }, isPrepaid ? null : 'paymentConfirmation'));
	} else if (viewType === 'payment') {
		dispatch(push({ pathname : '/payments/invoice' }));
	}
});

const updateCheckboxValue = (index, newValue, viewType) => ((dispatch, getState) => {
	const { data : { listData : data }, selectAll, totalCountForCheckbox } = getState().payments;
	const newSelectAll = cloneDeep(selectAll);
	const updatedData = cloneDeep(data);
	updatedData[index].Checked = newValue;
	const newArray = filter(updatedData, { Checked : true });
	if (totalCountForCheckbox === newArray.length) newSelectAll[viewType] = true;
	else newSelectAll[viewType] = false;
	const invoiceIds = newArray.map(({ Id }) => (Id));
	dispatch(Actions.updateFilters([{ property : 'id', value : invoiceIds }]));
	dispatch(Actions.receiveData([{ property : 'listData', value : updatedData }]));
	dispatch(Actions.updateStoreProperties({ selectAll : newSelectAll }));
});

const updateSelectAllValue = (viewType, newValue) => ((dispatch, getState) => {
	const { data : { listData : data }, selectAll } = getState().payments;
	const newSelectAll = cloneDeep(selectAll);
	const updatedData = cloneDeep(data);
	newSelectAll[viewType] = newValue;
	forEach(updatedData, (item) => {
		if (item.StatusId !== 5) item.Checked = newValue;
	});
	const invoiceIds = updatedData.filter((item) => item.Checked === true).map(({ Id }) => (Id));
	dispatch(Actions.updateFilters([{ property : 'id', value : invoiceIds }]));
	dispatch(Actions.receiveData([{ property : 'listData', value : updatedData }]));
	dispatch(Actions.updateStoreProperties({ selectAll : newSelectAll }));
});

const updateQueryString = (propertyObj) => ((dispatch, getState) => {
	const { router : { location : { search }, match : { params : { viewType } } } } = getState();
	const newQuery = queryString.parse(search);
	forEach(propertyObj, (item, key) => {
		newQuery[key] = item;
	});
	dispatch(push({ pathname : `/payments/list/${viewType}`, search : queryString.stringify(newQuery) }));
	dispatch(getInvoiceListData(viewType));
});

const updatePaymentsFilter = (obj) => ((dispatch, getState) => {
	const { filters } = getState().payments;
	forEach(obj, (item, key) => {
		filters[key] = item;
	});
	dispatch(Actions.updateStoreProperties({ filters }));
	dispatch(updateQueryString(obj));
});

const handleQueryStringOnBackButtonClick = (searchString) => ((dispatch) => {
	const updatedQueryString = queryString.parse(searchString);
	if (isEmpty(updatedQueryString)) dispatch(Actions.resetPaymentsFilter());
	else {
		const resetFilterArray = utils.queryStringDiff(updatedQueryString, PAYMENTS_FILTERS);
		dispatch(Actions.resetPaymentsFilter(resetFilterArray));
	}
	dispatch(getInvoiceListData());
});

const exportCSV = () => ((dispatch, getState) => {
	const {
		router: { match : { params : { viewType } } },
		payments : {
			filters : {
				sortedColumn, asc, locationId, id, clientId, jobStartDate, jobEndDate, invoiceStartDate, invoiceEndDate,
			},
		},
	} = getState();

	dispatch(Actions.setLoading('data', true));

	const status = constants.VIEW_INVOICE_STATUS[viewType].value;

	InvoiceService.exportCSV({
		status,
		sortBy : sortedColumn,
		asc,
		locationId,
		id,
		clientId,
		jobStartDate,
		jobEndDate,
		invoiceStartDate,
		invoiceEndDate,
	}).then(({ data }) => {
		const a = document.createElement('a');
		a.style.display = 'none';
		a.href = `data:attachment/csv,${encodeURIComponent(data)}`;
		a.target = '_blank';
		a.download = viewType === 'review' ? 'PendingReview.csv' : 'PendingPayments.csv';
		document.body.appendChild(a);

		a.click();
		dispatch(Actions.setLoading('data', false));
	}).catch(apiErrorHandler({
		dispatch,
		module   : 'payments',
		property : 'loading.data',
	}));
});

const downloadReceiptPDF = () => ((dispatch, getState) => {
	dispatch(Actions.setLoading('receipt', true));
	const { match : { params : { id } } } = getState().router;
	InvoiceService.downloadReceiptPDF({ id }).then(({ data }) => {
		utils.downloadReportFromBlobResponse(data, `rollkall_receipt_${id}.pdf`);
		dispatch(Actions.setLoading('receipt', false));
	}).catch(apiErrorHandler({
		dispatch,
		module   : 'payments',
		property : 'loading.receipt',
	}));
});

export default {
	getInvoiceListData,
	getClients,
	getProjects,
	getLocations,
	updatePaymentsFilter,
	getInvoiceData,
	updateCheckboxValue,
	updateSelectAllValue,
	invoiceStatusChanged,
	callStatusChangedApi,
	getInvoiceSummary,
	getReceiptDetails,
	handleQueryStringOnBackButtonClick,
	getDisputeReasons,
	submitDisputeForm,
	exportCSV,
	downloadReceiptPDF,
	applyCriteria,
	processUrl,
	resetCriteria,
};
